import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Multi-tenant scope. Staff/admins leave this null; client users set { role: 'client', slug: 'northwind-corp' };
// auditors set { role: 'auditor', token: '<one-time>', slug, allowed_areas } — token sent as X-Auditor-Token header.
// Persisted in localStorage so 'View as client' / auditor session survives page reloads.
const SCOPE_KEY = "isoquest.tenantScope";
export const getTenantScope = () => {
  try { return JSON.parse(localStorage.getItem(SCOPE_KEY) || "null"); } catch { return null; }
};
export const setTenantScope = (scope) => {
  if (scope) localStorage.setItem(SCOPE_KEY, JSON.stringify(scope));
  else localStorage.removeItem(SCOPE_KEY);
  window.dispatchEvent(new Event("isoquest:scope-changed"));
};
export const isReadOnly = () => getTenantScope()?.role === "auditor";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const scope = getTenantScope();
  if (scope?.role === "auditor") {
    if (scope.token) config.headers["X-Auditor-Token"] = scope.token;
  } else {
    if (scope?.role) config.headers["X-User-Role"] = scope.role;
    if (scope?.slug) config.headers["X-Client-Scope"] = scope.slug;
  }
  try {
    const acc = JSON.parse(localStorage.getItem("isoquest.activeAccountEmail") || "null");
    if (acc) config.headers["X-User-Email"] = acc;
  } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem("isoquest.auth") || sessionStorage.getItem("isoquest.auth.session");
    const authObj = raw ? JSON.parse(raw) : null;
    if (authObj?.access_token) {
      config.headers["Authorization"] = `Bearer ${authObj.access_token}`;
      if (!config.headers["X-User-Email"] && authObj.email) config.headers["X-User-Email"] = authObj.email;
    }
  } catch { /* ignore */ }
  return config;
});

export const getActiveAccountEmail = () => {
  try { return JSON.parse(localStorage.getItem("isoquest.activeAccountEmail") || "null"); } catch { return null; }
};
export const setActiveAccountEmail = (email) => {
  if (email) localStorage.setItem("isoquest.activeAccountEmail", JSON.stringify(email));
  else localStorage.removeItem("isoquest.activeAccountEmail");
  window.dispatchEvent(new Event("isoquest:account-changed"));
};

export const endpoints = {
  me: () => api.get("/me").then((r) => r.data),
  summary: () => api.get("/summary").then((r) => r.data),

  listAppointments: () => api.get("/appointments").then((r) => r.data),
  createAppointment: (payload) => api.post("/appointments", payload).then((r) => r.data),
  updateAppointment: (id, payload) => api.patch(`/appointments/${id}`, payload).then((r) => r.data),
  deleteAppointment: (id) => api.delete(`/appointments/${id}`).then((r) => r.data),
  listConsultants: () => api.get("/consultants").then((r) => r.data),
  listAppointmentTypes: () => api.get("/appointment-types").then((r) => r.data),

  listEmployeeRequests: (params = {}) => api.get("/employee-requests", { params }).then((r) => r.data),
  createEmployeeRequest: (payload) => api.post("/employee-requests", payload).then((r) => r.data),
  decideEmployeeRequest: (id, payload) => api.patch(`/employee-requests/${id}/decision`, payload).then((r) => r.data),
  deleteEmployeeRequest: (id) => api.delete(`/employee-requests/${id}`).then((r) => r.data),
  requestCategories: () => api.get("/request-categories").then((r) => r.data),

  listInvoices: (params = {}) => api.get("/invoices", { params }).then((r) => r.data),
  createInvoice: (payload) => api.post("/invoices", payload).then((r) => r.data),
  updateInvoice: (id, payload) => api.patch(`/invoices/${id}`, payload).then((r) => r.data),
  deleteInvoice: (id) => api.delete(`/invoices/${id}`).then((r) => r.data),
  clientBillingHistory: (client) => api.get(`/invoices/client/${encodeURIComponent(client)}`).then((r) => r.data),
  billingSummary: () => api.get("/billing/summary").then((r) => r.data),
  paymentStatuses: () => api.get("/payment-statuses").then((r) => r.data),

  prepareReminder: (payload) => api.post("/payment-reminders/prepare", payload).then((r) => r.data),
  listReminders: (params = {}) => api.get("/payment-reminders", { params }).then((r) => r.data),
  updateReminder: (id, payload) => api.patch(`/payment-reminders/${id}`, payload).then((r) => r.data),
  approveAndSendReminder: (id, payload) => api.post(`/payment-reminders/${id}/approve-and-send`, payload).then((r) => r.data),
  cancelReminder: (id) => api.post(`/payment-reminders/${id}/cancel`).then((r) => r.data),

  listClients: (q) => api.get("/clients", { params: q ? { q } : {} }).then((r) => r.data),
  getClient: (slug) => api.get(`/clients/${slug}`).then((r) => r.data),
  createClient: (payload) => api.post("/clients", payload).then((r) => r.data),
  updateClient: (slug, payload) => api.patch(`/clients/${slug}`, payload).then((r) => r.data),
  deleteClient: (slug) => api.delete(`/clients/${slug}`).then((r) => r.data),
  clientPortal: (slug) => api.get(`/clients/${slug}/portal`).then((r) => r.data),
  isoStandards: () => api.get("/iso-standards").then((r) => r.data),

  listNcrs: (params = {}) => api.get("/ncrs", { params }).then((r) => r.data),
  getNcr: (id) => api.get(`/ncrs/${id}`).then((r) => r.data),
  createNcr: (payload) => api.post("/ncrs", payload).then((r) => r.data),
  updateNcr: (id, payload) => api.patch(`/ncrs/${id}`, payload).then((r) => r.data),
  deleteNcr: (id) => api.delete(`/ncrs/${id}`).then((r) => r.data),
  ncrSources: () => api.get("/ncr-sources").then((r) => r.data),
  uploadNcrEvidence: (id, file, uploadedBy) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploaded_by", uploadedBy || "");
    return api.post(`/ncrs/${id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteNcrEvidence: (id, evId) => api.delete(`/ncrs/${id}/evidence/${evId}`).then((r) => r.data),
  ncrEvidenceUrl: (id, evId) => `${API}/ncrs/${id}/evidence/${evId}`,
  exportNcrPdfUrl: (id) => `${API}/ncrs/${id}/export.pdf`,
  exportNcrsCsvUrl: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/ncrs-export.csv${q ? `?${q}` : ""}`;
  },

  listObjectives: (params = {}) => api.get("/objectives", { params }).then((r) => r.data),
  getObjective: (id) => api.get(`/objectives/${id}`).then((r) => r.data),
  createObjective: (payload) => api.post("/objectives", payload).then((r) => r.data),
  updateObjective: (id, payload) => api.patch(`/objectives/${id}`, payload).then((r) => r.data),
  deleteObjective: (id) => api.delete(`/objectives/${id}`).then((r) => r.data),
  upsertMeasurement: (id, payload) => api.post(`/objectives/${id}/measurement`, payload).then((r) => r.data),
  addObjectiveComment: (id, payload) => api.post(`/objectives/${id}/comment`, payload).then((r) => r.data),
  objectiveMetadata: () => api.get("/objective-metadata").then((r) => r.data),
  objectiveYears: () => api.get("/objective-years").then((r) => r.data),
  uploadObjectiveEvidence: (id, file, uploadedBy) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("uploaded_by", uploadedBy || "");
    return api.post(`/objectives/${id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteObjectiveEvidence: (id, evId) => api.delete(`/objectives/${id}/evidence/${evId}`).then((r) => r.data),
  objectiveEvidenceUrl: (id, evId) => `${API}/objectives/${id}/evidence/${evId}`,
  exportObjectivePdfUrl: (id) => `${API}/objectives/${id}/export.pdf`,
  exportObjectivesCsvUrl: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/objectives-export.csv${q ? `?${q}` : ""}`;
  },

  listSuppliers: (params = {}) => api.get("/suppliers", { params }).then((r) => r.data),
  getSupplier: (id) => api.get(`/suppliers/${id}`).then((r) => r.data),
  createSupplier: (payload) => api.post("/suppliers", payload).then((r) => r.data),
  updateSupplier: (id, payload) => api.patch(`/suppliers/${id}`, payload).then((r) => r.data),
  deleteSupplier: (id) => api.delete(`/suppliers/${id}`).then((r) => r.data),
  addSupplierEvaluation: (id, payload) => api.post(`/suppliers/${id}/evaluations`, payload).then((r) => r.data),
  deleteSupplierEvaluation: (id, evId) => api.delete(`/suppliers/${id}/evaluations/${evId}`).then((r) => r.data),
  supplierMetadata: () => api.get("/supplier-metadata").then((r) => r.data),
  exportSupplierPdfUrl: (id) => `${API}/suppliers/${id}/export.pdf`,
  exportSuppliersCsvUrl: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/suppliers-export.csv${q ? `?${q}` : ""}`;
  },

  listEmployees: (params = {}) => api.get("/employees", { params }).then((r) => r.data),
  getEmployee: (id) => api.get(`/employees/${id}`).then((r) => r.data),
  createEmployee: (payload) => api.post("/employees", payload).then((r) => r.data),
  updateEmployee: (id, payload) => api.patch(`/employees/${id}`, payload).then((r) => r.data),
  deleteEmployee: (id) => api.delete(`/employees/${id}`).then((r) => r.data),
  addTraining: (id, payload) => api.post(`/employees/${id}/trainings`, payload).then((r) => r.data),
  updateTraining: (id, tid, payload) => api.patch(`/employees/${id}/trainings/${tid}`, payload).then((r) => r.data),
  deleteTraining: (id, tid) => api.delete(`/employees/${id}/trainings/${tid}`).then((r) => r.data),
  uploadCertificate: (id, tid, file, uploadedBy) => {
    const fd = new FormData(); fd.append("file", file); fd.append("uploaded_by", uploadedBy || "");
    return api.post(`/employees/${id}/trainings/${tid}/certificate`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  certificateUrl: (id, tid) => `${API}/employees/${id}/trainings/${tid}/certificate`,
  employeeMetadata: () => api.get("/employee-metadata").then((r) => r.data),
  trainingsCatalog: (params = {}) => api.get("/trainings-catalog", { params }).then((r) => r.data),
  trainingAlerts: (params = {}) => api.get("/training-alerts", { params }).then((r) => r.data),
  competencyMatrix: (params = {}) => api.get("/competency-matrix", { params }).then((r) => r.data),
  getTrainingReminderDays: () => api.get("/settings/training-reminders").then((r) => r.data),
  setTrainingReminderDays: (days) => api.patch("/settings/training-reminders", { training_reminder_days: days }).then((r) => r.data),
  exportEmployeesCsvUrl: (params = {}) => { const q = new URLSearchParams(params).toString(); return `${API}/employees-export.csv${q ? `?${q}` : ""}`; },
  exportCompetencyMatrixCsvUrl: (params = {}) => { const q = new URLSearchParams(params).toString(); return `${API}/competency-matrix.csv${q ? `?${q}` : ""}`; },

  listTasks: (assignee) => api.get("/tasks", { params: assignee ? { assignee } : {} }).then((r) => r.data),
  createTask: (payload) => api.post("/tasks", payload).then((r) => r.data),
  updateTask: (id, payload) => api.patch(`/tasks/${id}`, payload).then((r) => r.data),
  deleteTask: (id) => api.delete(`/tasks/${id}`).then((r) => r.data),
  listTaskStatuses: () => api.get("/task-statuses").then((r) => r.data),

  listReminders: () => api.get("/reminders").then((r) => r.data),
  createReminder: (payload) => api.post("/reminders", payload).then((r) => r.data),
  deleteReminder: (id) => api.delete(`/reminders/${id}`).then((r) => r.data),

  // Controlled documents
  documentMetadata: () => api.get("/document-metadata").then((r) => r.data),
  listDocuments: (params = {}) => api.get("/documents", { params }).then((r) => r.data),
  listWrittenDocs: (params = {}) => api.get("/written-documents", { params }).then((r) => r.data),
  writtenDocTemplates: () => api.get("/written-documents/templates").then((r) => r.data),
  getWrittenDoc: (id) => api.get(`/written-documents/${id}`).then((r) => r.data),
  createWrittenDoc: (payload) => api.post("/written-documents", payload).then((r) => r.data),
  updateWrittenDoc: (id, payload) => api.patch(`/written-documents/${id}`, payload).then((r) => r.data),
  deleteWrittenDoc: (id) => api.delete(`/written-documents/${id}`).then((r) => r.data),
  exportWrittenDoc: (id) => api.get(`/written-documents/${id}/export.docx`, { responseType: "blob" }).then((r) => {
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "document.docx";
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  }),
  getDocument: (id) => api.get(`/documents/${id}`).then((r) => r.data),
  createDocument: (formData) => api.post("/documents", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  updateDocument: (id, payload) => api.patch(`/documents/${id}`, payload).then((r) => r.data),
  deleteDocument: (id) => api.delete(`/documents/${id}`).then((r) => r.data),
  addDocumentRevision: (id, formData) => api.post(`/documents/${id}/revisions`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data),
  submitDocumentRevision: (id, revId, payload) => api.post(`/documents/${id}/revisions/${revId}/submit`, payload).then((r) => r.data),
  approveDocumentRevision: (id, revId, payload) => api.post(`/documents/${id}/revisions/${revId}/approve`, payload).then((r) => r.data),
  rejectDocumentRevision: (id, revId, payload) => api.post(`/documents/${id}/revisions/${revId}/reject`, payload).then((r) => r.data),
  documentAlerts: (params = {}) => api.get("/documents-alerts", { params }).then((r) => r.data),
  sendDocumentReviewAlert: (id, payload) => api.post(`/documents/${id}/send-review-alert`, payload).then((r) => r.data),
  documentCurrentUrl: (id) => `${API}/documents/${id}/current`,
  documentRevisionFileUrl: (id, revId) => `${API}/documents/${id}/revisions/${revId}/file`,
  exportDocumentsCsvUrl: (params = {}) => { const q = new URLSearchParams(params).toString(); return `${API}/documents-export.csv${q ? `?${q}` : ""}`; },

  // Assistance requests (client -> ISOQuest)
  assistanceMetadata: () => api.get("/assistance-metadata").then((r) => r.data),
  listAssistanceRequests: (params = {}) => api.get("/assistance-requests", { params }).then((r) => r.data),
  getAssistanceRequest: (id) => api.get(`/assistance-requests/${id}`).then((r) => r.data),
  createAssistanceRequest: (payload) => api.post("/assistance-requests", payload).then((r) => r.data),
  updateAssistanceRequest: (id, payload) => api.patch(`/assistance-requests/${id}`, payload).then((r) => r.data),
  acceptAssistanceRequest: (id, payload) => api.post(`/assistance-requests/${id}/accept`, payload).then((r) => r.data),
  addAssistanceNote: (id, payload) => api.post(`/assistance-requests/${id}/notes`, payload).then((r) => r.data),
  deleteAssistanceRequest: (id) => api.delete(`/assistance-requests/${id}`).then((r) => r.data),
  deleteDeadline: (id) => api.delete(`/deadlines/${id}`).then((r) => r.data),
  clearAllDeadlines: () => api.delete(`/deadlines`).then((r) => r.data),
  deleteNotification: (id) => api.delete(`/notifications/${id}`).then((r) => r.data),
  clearAllNotifications: () => api.delete(`/notifications`).then((r) => r.data),
  deleteClientRequest: (id) => api.delete(`/requests/${id}`).then((r) => r.data),
  exportAssistanceCsvUrl: (params = {}) => { const q = new URLSearchParams(params).toString(); return `${API}/assistance-requests-export.csv${q ? `?${q}` : ""}`; },

  // Auditor invites
  auditorMetadata: () => api.get("/auditor-metadata").then((r) => r.data),
  listAuditorInvites: (params = {}) => api.get("/auditor-invites", { params }).then((r) => r.data),
  getAuditorInvite: (id) => api.get(`/auditor-invites/${id}`).then((r) => r.data),
  createAuditorInvite: (payload) => api.post("/auditor-invites", payload).then((r) => r.data),
  revokeAuditorInvite: (id, payload) => api.post(`/auditor-invites/${id}/revoke`, payload).then((r) => r.data),
  deleteAuditorInvite: (id) => api.delete(`/auditor-invites/${id}`).then((r) => r.data),
  auditorSession: (token) => api.get("/auditor-session", { params: { token } }).then((r) => r.data),
  auditorLog: (area, details = "") => api.post("/auditor-log", { area, details }).then((r) => r.data).catch(() => null),

  listRequests: () => api.get("/requests").then((r) => r.data),
  createRequest: (payload) => api.post("/requests", payload).then((r) => r.data),

  listDeadlines: () => api.get("/deadlines").then((r) => r.data),
  listNotifications: () => api.get("/notifications").then((r) => r.data),

  uploadClientLogo: (slug, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/clients/${slug}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  clearClientLogo: (slug) => api.delete(`/clients/${slug}/logo`).then((r) => r.data),

  // Account management
  modulesCatalog: () => api.get("/accounts/modules-catalog").then((r) => r.data),
  listAccounts: (params = {}) => api.get("/accounts", { params }).then((r) => r.data),
  createAccount: (payload) => api.post("/accounts", payload).then((r) => r.data),
  updateAccount: (id, payload) => api.patch(`/accounts/${id}`, payload).then((r) => r.data),
  deleteAccount: (id) => api.delete(`/accounts/${id}`).then((r) => r.data),
  resendAccountInvite: (id) => api.post(`/accounts/${id}/resend-invite`).then((r) => r.data),
  myPermissions: () => api.get("/me/permissions").then((r) => r.data),

  // Auth
  authLogin: (payload) => api.post("/auth/login", payload).then((r) => r.data),
  authMe: () => api.get("/auth/me").then((r) => r.data),
  authLogout: () => api.post("/auth/logout").then((r) => r.data).catch(() => ({ ok: true })),
  authChangePassword: (payload) => api.post("/auth/change-password", payload).then((r) => r.data),

  // ISOQuest subscription basics (admin billing)
  listSubscriptionPlans: (params = {}) => api.get("/subscription-plans", { params }).then((r) => r.data),
  createSubscriptionPlan: (payload) => api.post("/subscription-plans", payload).then((r) => r.data),
  updateSubscriptionPlan: (id, payload) => api.patch(`/subscription-plans/${id}`, payload).then((r) => r.data),
  deleteSubscriptionPlan: (id) => api.delete(`/subscription-plans/${id}`).then((r) => r.data),
  listBankAccounts: (params = {}) => api.get("/bank-accounts", { params }).then((r) => r.data),
  createBankAccount: (payload) => api.post("/bank-accounts", payload).then((r) => r.data),
  updateBankAccount: (id, payload) => api.patch(`/bank-accounts/${id}`, payload).then((r) => r.data),
  deleteBankAccount: (id) => api.delete(`/bank-accounts/${id}`).then((r) => r.data),
  listSubscriptions: (params = {}) => api.get("/subscriptions", { params }).then((r) => r.data),
  createSubscription: (payload) => api.post("/subscriptions", payload).then((r) => r.data),
  updateSubscription: (id, payload) => api.patch(`/subscriptions/${id}`, payload).then((r) => r.data),
  deleteSubscription: (id) => api.delete(`/subscriptions/${id}`).then((r) => r.data),
  markSubscriptionPaid: (id) => api.post(`/subscriptions/${id}/mark-paid`).then((r) => r.data),
  getClientSubscription: (slug) => api.get(`/clients/${slug}/subscription`).then((r) => r.data),
  toggleClientAccess: (slug, payload) => api.post(`/clients/${slug}/access`, payload).then((r) => r.data),
  setDebitMandate: (slug, payload) => api.post(`/clients/${slug}/subscription/mandate`, payload).then((r) => r.data),
  cancelDebitMandate: (slug) => api.post(`/clients/${slug}/subscription/mandate/cancel`).then((r) => r.data),

  // Client ISO Task List (per-client, tenant-scoped)
  isoTasksMetadata: () => api.get("/iso-tasks-metadata").then((r) => r.data),
  listIsoTasks: (params = {}) => api.get("/iso-tasks", { params }).then((r) => r.data),
  createIsoTask: (payload) => api.post("/iso-tasks", payload).then((r) => r.data),
  updateIsoTask: (id, payload) => api.patch(`/iso-tasks/${id}`, payload).then((r) => r.data),
  deleteIsoTask: (id) => api.delete(`/iso-tasks/${id}`).then((r) => r.data),
  isoTasksDashboard: (client_slug) => api.get("/iso-tasks-dashboard", { params: { client_slug } }).then((r) => r.data),
  uploadIsoTaskEvidence: (id, file, uploadedBy) => {
    const fd = new FormData(); fd.append("file", file); if (uploadedBy) fd.append("uploaded_by", uploadedBy);
    return api.post(`/iso-tasks/${id}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteIsoTaskEvidence: (id, evId) => api.delete(`/iso-tasks/${id}/evidence/${evId}`).then((r) => r.data),
  isoTaskEvidenceUrl: (id, evId) => `${API}/iso-tasks/${id}/evidence/${evId}`,

  // Legal & Regulatory Register
  legalMetadata: () => api.get("/legal-metadata").then((r) => r.data),
  listLegalRequirements: (params = {}) => api.get("/legal-requirements", { params }).then((r) => r.data),
  getLegalRequirement: (id) => api.get(`/legal-requirements/${id}`).then((r) => r.data),
  createLegalRequirement: (payload) => api.post("/legal-requirements", payload).then((r) => r.data),
  updateLegalRequirement: (id, payload) => api.patch(`/legal-requirements/${id}`, payload).then((r) => r.data),
  deleteLegalRequirement: (id) => api.delete(`/legal-requirements/${id}`).then((r) => r.data),
  legalDashboard: (client_slug) => api.get("/legal-dashboard", { params: { client_slug } }).then((r) => r.data),
};
